import {
    AppOrderPaymentTermsEnum,
    SupplierPayoutTermsEnum,
    PayInStateEnum,
    PayoutStateEnum,
    DeliveryStateEnum,
    ShippingResponsibilityEnum,
} from "@/types/api/data-contracts";

export interface TargetStates {
    buyerPayment: PayInStateEnum;
    supplierPayment: PayoutStateEnum;
    delivery: DeliveryStateEnum;
}

export const BEFORE_DISPATCH_PAY_IN = new Set<AppOrderPaymentTermsEnum>([
    AppOrderPaymentTermsEnum.PayBeforeDispatch,
]);

/**
 * Return the target (buyerPayment, supplierPayment, delivery) states
 * for a given combination of shipping responsibility, payment terms,
 * and payout terms.
 *
 * Implements the 16-row transition table from SC-2402.
 * Pure function — no side effects.
 * 
 * ### Possible change results:
| # | Shipping Responsibility | Payment Terms (Buyer) | Payout Terms (Supplier) | Buyer Payment State | Supplier Payment State | Delivery State |
|---|---|---|---|---|---|---|
| 1 | supplier | pay_before_dispatch | payout_before_dispatch | due | due | not_started |
| 2 | supplier | pay_before_dispatch | payout_after_dispatch | due | not_started | not_started |
| 3 | supplier | pay_after_dispatch | payout_before_dispatch | not_started | due | not_started |
| 4 | supplier | pay_after_dispatch | payout_after_dispatch | not_started | not_started | pending_dispatch |
| 5 | buyer | pay_before_dispatch | payout_before_dispatch | due | due | not_started |
| 6 | buyer | pay_before_dispatch | payout_after_dispatch | due | not_started | not_started |
| 7 | buyer | pay_after_dispatch | payout_before_dispatch | not_started | due | not_started |
| 8 | buyer | pay_after_dispatch | payout_after_dispatch | not_started | not_started | awaiting_collection_details |
| 9 | merchant_of_record | pay_before_dispatch | payout_before_dispatch | not_started | due | gathering_freight_quotes |
| 10 | merchant_of_record | pay_before_dispatch | payout_after_dispatch | not_started | not_started | gathering_freight_quotes |
| 11 | merchant_of_record | pay_after_dispatch | payout_before_dispatch | not_started | due | not_started |
| 12 | merchant_of_record | pay_after_dispatch | payout_after_dispatch | not_started | not_started | preparing_freight |
| 13 | to_be_confirmed | pay_before_dispatch | payout_before_dispatch | due | due | not_started |
| 14 | to_be_confirmed | pay_before_dispatch | payout_after_dispatch | due | not_started | not_started |
| 15 | to_be_confirmed | pay_after_dispatch | payout_before_dispatch | not_started | due | not_started |
| 16 | to_be_confirmed | pay_after_dispatch | payout_after_dispatch | not_started | not_started | not_started |
 */
export function getTargetStates(
    shippingResponsibility: ShippingResponsibilityEnum,
    paymentTerms: AppOrderPaymentTermsEnum,
    payoutTerms: SupplierPayoutTermsEnum
): TargetStates {
    const isSupplier =
        shippingResponsibility === ShippingResponsibilityEnum.Supplier;
    const isBuyer = shippingResponsibility === ShippingResponsibilityEnum.Buyer;
    const isMor =
        shippingResponsibility === ShippingResponsibilityEnum.MerchantOfRecord;
    const isTbc =
        shippingResponsibility === ShippingResponsibilityEnum.ToBeConfirmed;
    const isPayBefore = BEFORE_DISPATCH_PAY_IN.has(paymentTerms);
    const isPayoutBefore =
        payoutTerms === SupplierPayoutTermsEnum.PayoutBeforeDispatch;

    const DUE_PAY_IN = PayInStateEnum.Due;
    const NOT_STARTED_PAY_IN = PayInStateEnum.NotStarted;
    const DUE_PAYOUT = PayoutStateEnum.Due;
    const NOT_STARTED_PAYOUT = PayoutStateEnum.NotStarted;

    // Row 1: supplier + pay_before + payout_before
    if (isSupplier && isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 2: supplier + pay_before + payout_after
    if (isSupplier && isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 3: supplier + pay_after + payout_before
    if (isSupplier && !isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 4: supplier + pay_after + payout_after
    if (isSupplier && !isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.PendingDispatch,
        };
    }

    // Row 5: buyer + pay_before + payout_before
    if (isBuyer && isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 6: buyer + pay_before + payout_after
    if (isBuyer && isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 7: buyer + pay_after + payout_before
    if (isBuyer && !isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 8: buyer + pay_after + payout_after
    if (isBuyer && !isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.AwaitingCollectionDetails,
        };
    }

    // Row 9: MoR + pay_before + payout_before
    if (isMor && isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.GatheringFreightQuotes,
        };
    }

    // Row 10: MoR + pay_before + payout_after
    if (isMor && isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.GatheringFreightQuotes,
        };
    }

    // Row 11: MoR + pay_after + payout_before
    if (isMor && !isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 12: MoR + pay_after + payout_after
    if (isMor && !isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.PreparingFreight,
        };
    }

    // Row 13: to_be_confirmed + pay_before + payout_before
    if (isTbc && isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 14: to_be_confirmed + pay_before + payout_after
    if (isTbc && isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: DUE_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 15: to_be_confirmed + pay_after + payout_before
    if (isTbc && !isPayBefore && isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: DUE_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    // Row 16: to_be_confirmed + pay_after + payout_after
    if (isTbc && !isPayBefore && !isPayoutBefore) {
        return {
            buyerPayment: NOT_STARTED_PAY_IN,
            supplierPayment: NOT_STARTED_PAYOUT,
            delivery: DeliveryStateEnum.NotStarted,
        };
    }

    throw new Error(
        `Unhandled combination: shipping=${shippingResponsibility}, payment=${paymentTerms}, payout=${payoutTerms}`
    );
}
